import {
    IonAvatar,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    chevronForwardOutline,
    americanFootballOutline,
    refreshOutline,
    statsChartOutline,
    trophyOutline
} from "ionicons/icons";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Components from "./StatsFunction";
import {
    CompetitionDetail,
    CompetitionGame,
    CompetitionTeam,
    GamePlay,
    Team,
    getCompetitionDetail,
    getGameDetail,
    getHomeData
} from "../services/mexfbaApi";
import "./ResultStatsTeam.css";

interface RouteParams {
    id: string;
}

type TeamStatCategory = "offense" | "defense" | "special";

type LegacyTeamStat = Record<string, string | number | undefined>;

interface StatColumn {
    label: string;
    value: (stat: LegacyTeamStat) => string | number;
}

interface TeamStatRow {
    id: string;
    nombre: string;
    logo?: string;
    color?: string;
    values: Array<string | number>;
    primaryValue: number;
}

function getNumber(value: unknown): number {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function getValue(
    stat: LegacyTeamStat,
    property: string
): string | number {
    return stat[property] ?? 0;
}

function getDefenseId(
    offensiveTeamId: string | undefined,
    localTeamId: string,
    visitorTeamId: string
): string {
    return offensiveTeamId === localTeamId
        ? visitorTeamId
        : localTeamId;
}

function getGamePlays(
    plays?: GamePlay[] | Record<string, GamePlay>
): GamePlay[] {
    if (!plays) {
        return [];
    }

    return Array.isArray(plays)
        ? plays
        : Object.values(plays);
}

function getCompetitionGames(
    competition: CompetitionDetail
): CompetitionGame[] {
    const gamesById = new Map<string, CompetitionGame>();

    /*
        Sólo se consideran partidos de fase de grupos.
        Los encuentros de playoffs, semifinales, finales u otra fase
        quedan excluidos aunque existan dentro de Partidos o Final.
    */
    Object.values(competition.Partidos || {}).forEach(function (game) {
        if (
            game?.id &&
            String(game.partido || "").trim() === "grupos"
        ) {
            gamesById.set(String(game.id), game);
        }
    });

    return Array.from(gamesById.values());
}

const ResultStatsTeam: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [competition, setCompetition] =
        useState<CompetitionDetail | null>(null);

    const [teams, setTeams] = useState<Team[]>([]);
    const [teamStats, setTeamStats] =
        useState<LegacyTeamStat[]>([]);

    const [activeCategory, setActiveCategory] =
        useState<TeamStatCategory>("offense");

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadStatistics = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [competitionData, homeData] =
                    await Promise.all([
                        getCompetitionDetail(id, forceRefresh),
                        getHomeData(forceRefresh)
                    ]);

                const competitionTeams: CompetitionTeam[] =
                    Object.values(competitionData.Equipos || {});

                const aggregatedStats: LegacyTeamStat[] = [];

                /*
                    Inicializa una fila de estadísticas para cada equipo
                    usando la función histórica de StatsFunction.
                */
                Components.setStatsTeam(
                    competitionTeams,
                    aggregatedStats
                );

                const games = getCompetitionGames(competitionData);

                await Promise.all(
                    games.map(async function (game) {
                        try {
                            const gameDetail = await getGameDetail(
                                game.id,
                                forceRefresh
                            );

                            const localTeamId =
                                gameDetail.Data.id_local ||
                                game.id_local;

                            const visitorTeamId =
                                gameDetail.Data.id_visita ||
                                game.id_visita;

                            getGamePlays(
                                gameDetail.Jugadas
                            ).forEach(function (play) {
                                const defenseId = getDefenseId(
                                    play.ofensiva,
                                    localTeamId,
                                    visitorTeamId
                                );

                                /*
                                    Se conserva la lógica de cálculo
                                    original del proyecto.
                                */
                                Components.getOfence(
                                    play,
                                    aggregatedStats
                                );

                                Components.getDefencee(
                                    play,
                                    aggregatedStats,
                                    defenseId
                                );

                                Components.getSpecialK(
                                    play,
                                    aggregatedStats
                                );

                                Components.getSpecialR(
                                    play,
                                    aggregatedStats,
                                    defenseId
                                );
                            });
                        } catch (gameError) {
                            /*
                                Un archivo de partido faltante no evita
                                mostrar las estadísticas disponibles.
                            */
                            console.warn(
                                `No fue posible abrir el partido ${game.id}.`,
                                gameError
                            );
                        }
                    })
                );

                setCompetition(competitionData);
                setTeams(homeData.teams);
                setTeamStats(aggregatedStats);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar estadísticas de equipos:",
                    requestError
                );

                setError(
                    "No fue posible calcular las estadísticas de los equipos."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadStatistics();
        },
        [loadStatistics]
    );

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team];
            })
        );
    }, [teams]);

    const competitionTeamsById = useMemo(function () {
        if (!competition) {
            return new Map<string, CompetitionTeam>();
        }

        return new Map(
            Object.values(competition.Equipos || {}).map(function (team) {
                return [String(team.id), team];
            })
        );
    }, [competition]);

    const categoryColumns = useMemo<
        Record<TeamStatCategory, StatColumn[]>
    >(
        function () {
            return {
                offense: [
                    {
                        label: "TOT",
                        value: function (stat) {
                            return (
                                getNumber(getValue(stat, "rush")) +
                                getNumber(getValue(stat, "pass"))
                            );
                        }
                    },
                    {
                        label: "RUSH",
                        value: function (stat) {
                            return getValue(stat, "rush");
                        }
                    },
                    {
                        label: "PASS",
                        value: function (stat) {
                            return getValue(stat, "pass");
                        }
                    },
                    {
                        label: "TD",
                        value: function (stat) {
                            return (
                                getNumber(getValue(stat, "tdR")) +
                                getNumber(getValue(stat, "tdP"))
                            );
                        }
                    },
                    {
                        label: "TD R",
                        value: function (stat) {
                            return getValue(stat, "tdR");
                        }
                    },
                    {
                        label: "TD P",
                        value: function (stat) {
                            return getValue(stat, "tdP");
                        }
                    },
                    {
                        label: "2PT",
                        value: function (stat) {
                            return getValue(stat, "conv");
                        }
                    }
                ],

                defense: [
                    {
                        label: "TOT",
                        value: function (stat) {
                            return (
                                getNumber(getValue(stat, "rushD")) +
                                getNumber(getValue(stat, "passD"))
                            );
                        }
                    },
                    {
                        label: "RUSH",
                        value: function (stat) {
                            return getValue(stat, "rushD");
                        }
                    },
                    {
                        label: "PASS",
                        value: function (stat) {
                            return getValue(stat, "passD");
                        }
                    },
                    {
                        label: "TD",
                        value: function (stat) {
                            return (
                                getNumber(getValue(stat, "tdRD")) +
                                getNumber(getValue(stat, "tdPD"))
                            );
                        }
                    },
                    {
                        label: "TD R",
                        value: function (stat) {
                            return getValue(stat, "tdRD");
                        }
                    },
                    {
                        label: "TD P",
                        value: function (stat) {
                            return getValue(stat, "tdPD");
                        }
                    },
                    {
                        label: "SACK",
                        value: function (stat) {
                            return getValue(stat, "sack");
                        }
                    },
                    {
                        label: "FR",
                        value: function (stat) {
                            return getValue(stat, "fr");
                        }
                    },
                    {
                        label: "INT",
                        value: function (stat) {
                            return getValue(stat, "int");
                        }
                    }
                ],

                special: [
                    {
                        label: "XP",
                        value: function (stat) {
                            return `${getValue(
                                stat,
                                "attXp"
                            )}/${getValue(stat, "xp")}`;
                        }
                    },
                    {
                        label: "FG",
                        value: function (stat) {
                            return `${getValue(
                                stat,
                                "attFg"
                            )}/${getValue(stat, "fg")}`;
                        }
                    },
                    {
                        label: "YDS KR/PR",
                        value: function (stat) {
                            return `${getValue(
                                stat,
                                "ydsKR"
                            )}/${getValue(stat, "ydsPR")}`;
                        }
                    },
                    {
                        label: "TD KR/PR",
                        value: function (stat) {
                            return `${getValue(
                                stat,
                                "tdKR"
                            )}/${getValue(stat, "tdPR")}`;
                        }
                    },
                    {
                        label: "BLK",
                        value: function (stat) {
                            return getValue(stat, "kb");
                        }
                    }
                ]
            };
        },
        []
    );

    const statRows = useMemo(function (): TeamStatRow[] {
    const columns = categoryColumns[activeCategory];

    const sortProperty: Record<TeamStatCategory, string> = {
        offense: "pass",
        defense: "passD",
        special: "ydsKR"
    };

    return teamStats
        .reduce<TeamStatRow[]>(
            function (rows, stat) {
                const teamId = String(
                    getValue(stat, "id") || ""
                ).trim();

                if (!teamId) {
                    return rows;
                }

                const registeredTeam = teamsById.get(teamId);
                const competitionTeam =
                    competitionTeamsById.get(teamId);

                const teamName = String(
    registeredTeam?.nombre ||
    competitionTeam?.nombre ||
    ""
).trim();

/*
    No se muestra ninguna fila si no existe un equipo válido o si
    el equipo no tiene un nombre que pueda mostrarse.
*/
if (!teamName) {
    return rows;
}

rows.push({
    id: teamId,
    nombre: teamName,

                    logo: registeredTeam?.logo,
                    color: registeredTeam?.color,
                    values: columns.map(function (column) {
                        return column.value(stat);
                    }),
                    primaryValue: getNumber(
                        getValue(
                            stat,
                            sortProperty[activeCategory]
                        )
                    )
                });

                return rows;
            },
            []
        )
        .sort(function (firstTeam, secondTeam) {
            return (
                secondTeam.primaryValue -
                firstTeam.primaryValue
            );
        });
}, [
    activeCategory,
    categoryColumns,
    competitionTeamsById,
    teamStats,
    teamsById
]);


    const categoryTitle: Record<TeamStatCategory, string> = {
        offense: "Ranking ofensivo",
        defense: "Ranking defensivo",
        special: "Equipos especiales"
    };

    const leader = statRows[0];

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadStatistics(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="result-team-stats-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !competition) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="result-team-stats-content">
                    <div className="result-team-stats-empty">
                        <IonIcon icon={statsChartOutline} />
                        <strong>
                            No fue posible cargar las estadísticas
                        </strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = competition.Info;
    const activeColumns = categoryColumns[activeCategory];

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="result-team-stats-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar a la competencia"
                            onClick={function () {
                                history.push(`/results/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Estadísticas</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar estadísticas"
                            disabled={refreshing}
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="result-team-stats-content"
            >
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Calculando estadísticas..."
                    />
                </IonRefresher>

                <section className="result-team-stats-hero">
                    {info.logo ? (
                        <img
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="result-team-stats-hero__placeholder">
                            <IonIcon icon={trophyOutline} />
                        </div>
                    )}

                    <div>
                        <span>ESTADÍSTICAS DE EQUIPO</span>
                        <h1>{info.nombre}</h1>
                        <p>
                            Comparativa colectiva de la competencia.
                        </p>
                    </div>
                </section>

                <section className="result-team-stats-navigation">
                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/calendar/${id}`);
                        }}
                    >
                        Calendario
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/results/${id}`);
                        }}
                    >
                        Tabla
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsResults/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton>Estadísticas</IonButton>
                </section>

                <section className="result-team-stats-section">
                    <div className="result-team-stats-heading">
                        <div>
                            <span>RANKING POR EQUIPO</span>
                            <h2>{categoryTitle[activeCategory]}</h2>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    <IonSegment
                        className="result-team-stats-filter"
                        value={activeCategory}
                        onIonChange={function (event) {
                            setActiveCategory(
                                (event.detail.value ||
                                    "offense") as TeamStatCategory
                            );
                        }}
                    >
                        <IonSegmentButton value="offense">
                            OFENSIVA
                        </IonSegmentButton>

                        <IonSegmentButton value="defense">
                            DEFENSIVA
                        </IonSegmentButton>

                        <IonSegmentButton value="special">
                            ESPECIALES
                        </IonSegmentButton>
                    </IonSegment>

                    {leader && (
                        <button
                            className="result-team-stats-leader"
                            type="button"
                            onClick={function () {
                                history.push(`/team/${leader.id}`);
                            }}
                        >
                            <IonAvatar
                                style={{
                                    background: leader.color || "#eaf2fb"
                                }}
                            >
                                {leader.logo ? (
                                    <img
                                        src={leader.logo}
                                        alt={`Logo de ${leader.nombre}`}
                                    />
                                ) : (
                                    <span>
                                        {leader.nombre
                                            .charAt(0)
                                            .toUpperCase()}
                                    </span>
                                )}
                            </IonAvatar>

                            <div>
                                <span>LÍDER ACTUAL</span>
                                <strong>{leader.nombre}</strong>
                                <small>
                                    {categoryTitle[activeCategory]}
                                </small>
                            </div>

                            <IonIcon icon={chevronForwardOutline} />
                        </button>
                    )}

                    {statRows.length === 0 ? (
                        <div className="result-team-stats-no-results">
                            <IonIcon icon={americanFootballOutline} />
                            <strong>
                                No hay estadísticas disponibles
                            </strong>
                            <span>
                                Aún no se han registrado jugadas para esta
                                competencia.
                            </span>
                        </div>
                    ) : (
                        <div className="result-team-stats-table-wrapper">
                            <table className="result-team-stats-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>EQUIPO</th>

                                        {activeColumns.map(
                                            function (column) {
                                                return (
                                                    <th
                                                        key={column.label}
                                                    >
                                                        {column.label}
                                                    </th>
                                                );
                                            }
                                        )}
                                    </tr>
                                </thead>

                                <tbody>
                                    {statRows.map(function (row, index) {
                                        return (
                                            <tr key={row.id}>
                                                <td className="result-team-stats-rank">
                                                    {index + 1}
                                                </td>

                                                <td>
                                                    <button
                                                        className="result-team-stats-team"
                                                        type="button"
                                                        onClick={function () {
                                                            history.push(
                                                                `/team/${row.id}`
                                                            );
                                                        }}
                                                    >
                                                        <IonAvatar
                                                            style={{
                                                                background:
                                                                    row.color ||
                                                                    "#eaf2fb"
                                                            }}
                                                        >
                                                            {row.logo ? (
                                                                <img
                                                                    src={
                                                                        row.logo
                                                                    }
                                                                    alt=""
                                                                />
                                                            ) : (
                                                                <span>
                                                                    {row.nombre
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase()}
                                                                </span>
                                                            )}
                                                        </IonAvatar>

                                                        <strong>
                                                            {row.nombre}
                                                        </strong>
                                                    </button>
                                                </td>

                                                {row.values.map(
                                                    function (
                                                        value,
                                                        valueIndex
                                                    ) {
                                                        return (
                                                            <td
                                                                key={`${row.id}-${valueIndex}`}
                                                            >
                                                                {value}
                                                            </td>
                                                        );
                                                    }
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default ResultStatsTeam;
